import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  Loader2, ChevronRight, ChevronDown, File, Folder, FolderOpen,
  X, Send, Copy, MessageSquare, PanelRightClose, PanelRightOpen, Save, Download,
  Check, Trash2, Paperclip, Maximize2, Minimize2,
} from 'lucide-react'
import CodeMirror from '@uiw/react-codemirror'
import { javascript } from '@codemirror/lang-javascript'
import { html } from '@codemirror/lang-html'
import { css } from '@codemirror/lang-css'
import { python } from '@codemirror/lang-python'
import { json } from '@codemirror/lang-json'
import { xml } from '@codemirror/lang-xml'
import { oneDark } from '@codemirror/theme-one-dark'
import type { Extension } from '@codemirror/state'
import { getTree, getFileContent, saveFile, deleteFile, aiSuggest, downloadAll, downloadFile } from '@/api/srceditor'
import { getSettings } from '@/api/settings'
import { useThemeStore } from '@/store/themeStore'
import { useProjectStore } from '@/store/projectStore'
import { useRunOnlyRedirect } from '@/auth/useRunOnlyRedirect'
import { useClaudeAgent } from '@/hooks/useClaudeAgent'
import { useAttachments } from '@/hooks/useAttachments'
import AttachmentChips from '@/components/common/AttachmentChips'
import { ATTACHMENT_ACCEPT, toWire } from '@/lib/attachments'
import type { FileNode } from '@/types'

interface OpenFile {
  path: string
  name: string
  content: string
  originalContent: string
  modified: boolean
  root: 'src' | 'project'
}

// Composite key to uniquely identify a file across both roots
function fileKey(root: 'src' | 'project', path: string): string {
  return `${root}:${path}`
}

interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
}

// Detect language extension from file name
function getLanguageExtension(fileName: string): Extension[] {
  const ext = fileName.split('.').pop()?.toLowerCase() || ''
  switch (ext) {
    case 'js':
    case 'jsx':
      return [javascript({ jsx: true })]
    case 'ts':
    case 'tsx':
      return [javascript({ jsx: true, typescript: true })]
    case 'html':
    case 'htm':
      return [html()]
    case 'css':
    case 'scss':
      return [css()]
    case 'py':
      return [python()]
    case 'json':
      return [json()]
    case 'xml':
    case 'xpdl':
    case 'svg':
      return [xml()]
    default:
      return []
  }
}

function getFileTypeLabel(fileName: string): string {
  const ext = fileName.split('.').pop()?.toLowerCase() || ''
  switch (ext) {
    case 'js': case 'jsx': return 'javascript'
    case 'ts': case 'tsx': return 'typescript'
    case 'html': case 'htm': return 'html'
    case 'css': case 'scss': return 'css'
    case 'py': return 'python'
    case 'json': return 'json'
    case 'xml': case 'xpdl': return 'xml'
    default: return 'text'
  }
}

// Check if file content is only comments (no actual code)
// Matches original logic: strip block comments, line comments, whitespace — if nothing left, it's comment-only
function isCommentOnlyContent(content: string): boolean {
  if (!content.trim()) return false
  // Remove block comments (/* ... */)
  let stripped = content.replace(/\/\*[\s\S]*?\*\//g, '')
  // Remove line comments (// ...)
  stripped = stripped.replace(/\/\/.*$/gm, '')
  // Remove Python-style comments (# ...)
  stripped = stripped.replace(/#.*$/gm, '')
  // Remove all whitespace
  stripped = stripped.replace(/\s/g, '')
  // If nothing left, it's comment-only
  return stripped.length === 0
}

// Extract code blocks from AI response
function extractCodeBlocks(text: unknown): { code: string; language: string; explanation: string }[] {
  const str = typeof text === 'string' ? text : String(text ?? '')
  const blocks: { code: string; language: string; explanation: string }[] = []
  const codeBlockRegex = /```(\w*)\n([\s\S]*?)```/g
  let lastIndex = 0
  let match

  while ((match = codeBlockRegex.exec(str)) !== null) {
    const explanation = str.substring(lastIndex, match.index).trim()
    blocks.push({ code: match[2].trim(), language: match[1] || '', explanation })
    lastIndex = match.index + match[0].length
  }

  if (blocks.length === 0) {
    blocks.push({ code: '', language: '', explanation: str })
  } else {
    const trailing = str.substring(lastIndex).trim()
    if (trailing) {
      blocks[blocks.length - 1].explanation += '\n' + trailing
    }
  }

  return blocks
}

// Simple markdown formatting for explanation text
function formatExplanation(text: unknown): string {
  const str = typeof text === 'string' ? text : String(text ?? '')
  return str
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code style="background:var(--color-bg);padding:1px 4px;border-radius:3px;font-size:0.8125rem">$1</code>')
}

// Recursive file tree component
function FileTreeNode({
  node,
  depth,
  onFileClick,
  onFileDelete,
  onFileDownload,
  expandedDirs,
  toggleDir,
}: {
  node: FileNode
  depth: number
  onFileClick: (node: FileNode) => void
  onFileDelete?: (node: FileNode) => void
  onFileDownload?: (node: FileNode) => void
  expandedDirs: Set<string>
  toggleDir: (path: string) => void
}) {
  const isExpanded = expandedDirs.has(node.path)

  if (node.is_dir) {
    return (
      <div>
        <button
          onClick={() => toggleDir(node.path)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.375rem',
            width: '100%',
            padding: '0.25rem 0.375rem',
            paddingLeft: `${depth * 16 + 4}px`,
            background: 'none',
            color: 'var(--color-text-secondary)',
            fontSize: '0.875rem',
            textAlign: 'left',
            borderRadius: 'var(--radius-sm)',
            transition: 'background var(--transition-fast)',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-bg-secondary)')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
        >
          {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          {isExpanded ? (
            <FolderOpen size={14} style={{ color: 'var(--color-warning, #9a6700)', flexShrink: 0 }} />
          ) : (
            <Folder size={14} style={{ color: 'var(--color-warning, #9a6700)', flexShrink: 0 }} />
          )}
          <span className="truncate">{node.name}</span>
        </button>
        {isExpanded && node.children && (
          <div>
            {node.children
              .slice()
              .sort((a, b) => {
                if (a.is_dir === b.is_dir) return a.name.localeCompare(b.name)
                return a.is_dir ? -1 : 1
              })
              .map((child) => (
                <FileTreeNode
                  key={child.path}
                  node={child}
                  depth={depth + 1}
                  onFileClick={onFileClick}
                  onFileDelete={onFileDelete}
                  onFileDownload={onFileDownload}
                  expandedDirs={expandedDirs}
                  toggleDir={toggleDir}
                />
              ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div
      className="file-tree-item"
      style={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        borderRadius: 'var(--radius-sm)',
        transition: 'background var(--transition-fast)',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-bg-secondary)')}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
    >
      <button
        onClick={() => onFileClick(node)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.375rem',
          flex: 1,
          minWidth: 0,
          padding: '0.25rem 0.375rem',
          paddingLeft: `${depth * 16 + 20}px`,
          background: 'none',
          color: 'var(--color-text-secondary)',
          fontSize: '0.875rem',
          textAlign: 'left',
        }}
      >
        <File size={13} style={{ flexShrink: 0, color: 'var(--color-accent)' }} />
        <span className="truncate">{node.name}</span>
      </button>
      {onFileDownload && (
        <button
          className="file-delete-btn"
          title="Download file"
          onClick={(e) => { e.stopPropagation(); onFileDownload(node) }}
          style={{
            display: 'none',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0.25rem',
            marginRight: '0.125rem',
            background: 'none',
            color: 'var(--color-text-tertiary)',
            borderRadius: 'var(--radius-sm)',
            flexShrink: 0,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--color-accent)')}
          onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--color-text-tertiary)')}
        >
          <Download size={13} />
        </button>
      )}
      {onFileDelete && (
        <button
          className="file-delete-btn"
          title="Delete file"
          onClick={(e) => { e.stopPropagation(); onFileDelete(node) }}
          style={{
            display: 'none',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0.25rem',
            marginRight: '0.25rem',
            background: 'none',
            color: 'var(--color-text-tertiary)',
            borderRadius: 'var(--radius-sm)',
            flexShrink: 0,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--color-danger, #ef4444)')}
          onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--color-text-tertiary)')}
        >
          <Trash2 size={13} />
        </button>
      )}
    </div>
  )
}

// ---- AI Chat Bubble with code block parsing ----
function AIChatBubble({
  message,
  onAcceptCode,
  onCopyCode,
  onDeleteMessage,
}: {
  message: ChatMessage
  onAcceptCode: (code: string) => void
  onCopyCode: (code: string) => void
  onDeleteMessage: () => void
}) {
  if (message.role === 'user') {
    return (
      <div style={{
        padding: '0.625rem 0.75rem',
        borderRadius: 'var(--radius-md)',
        background: 'var(--color-bg)',
        border: '1px solid var(--color-border)',
        fontSize: '0.875rem',
        lineHeight: 1.6,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={{
            fontSize: '0.8125rem', fontWeight: 600,
            color: 'var(--color-text-secondary)',
            textTransform: 'uppercase', letterSpacing: '0.03em',
          }}>You</span>
          <button
            onClick={onDeleteMessage}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--color-text-secondary)', padding: 2, display: 'flex',
            }}
            title="Delete message"
          >
            <Trash2 size={11} />
          </button>
        </div>
        <p style={{ margin: 0, color: 'var(--color-text)', whiteSpace: 'pre-wrap' }}>
          {message.content}
        </p>
      </div>
    )
  }

  // Assistant message: parse code blocks
  const blocks = extractCodeBlocks(message.content)
  const hasCode = blocks.some(b => b.code)

  return (
    <div style={{
      padding: '0.625rem 0.75rem',
      borderRadius: 'var(--radius-md)',
      background: 'rgba(76, 141, 255, 0.08)',
      border: '1px solid var(--color-accent-weak)',
      fontSize: '0.875rem',
      lineHeight: 1.6,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{
          fontSize: '0.8125rem', fontWeight: 600,
          color: 'var(--color-accent)',
          textTransform: 'uppercase', letterSpacing: '0.03em',
        }}>AI</span>
        <button
          onClick={onDeleteMessage}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--color-text-secondary)', padding: 2, display: 'flex',
          }}
          title="Delete message"
        >
          <Trash2 size={11} />
        </button>
      </div>

      {hasCode ? (
        blocks.map((block, i) => (
          <div key={i}>
            {block.explanation && (
              <p
                style={{
                  margin: '4px 0', color: 'var(--color-text)',
                  whiteSpace: 'pre-wrap', fontSize: '0.8425rem',
                }}
                dangerouslySetInnerHTML={{ __html: formatExplanation(block.explanation) }}
              />
            )}
            {block.code && (
              <div style={{
                marginTop: 6, borderRadius: 4,
                border: '1px solid var(--color-border)', overflow: 'hidden',
              }}>
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '3px 8px',
                  background: 'var(--color-bg)',
                  borderBottom: '1px solid var(--color-border)',
                }}>
                  {block.language && (
                    <span style={{
                      fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
                      textTransform: 'uppercase', letterSpacing: '0.05em',
                    }}>{block.language}</span>
                  )}
                  {!block.language && <span />}
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button
                      onClick={() => onAcceptCode(block.code)}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: 'var(--color-accent)', padding: 2, display: 'flex', alignItems: 'center', gap: 3,
                        fontSize: '0.8125rem',
                      }}
                      title="Accept - apply to editor"
                    >
                      <Check size={11} /> Accept
                    </button>
                    <button
                      onClick={() => onCopyCode(block.code)}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: 'var(--color-text-secondary)', padding: 2, display: 'flex', alignItems: 'center', gap: 3,
                        fontSize: '0.8125rem',
                      }}
                      title="Copy to clipboard"
                    >
                      <Copy size={11} /> Copy
                    </button>
                  </div>
                </div>
                <pre style={{
                  margin: 0, padding: '8px 10px',
                  background: '#1e1e1e',
                  fontSize: '0.8125rem',
                  fontFamily: 'monospace',
                  color: '#d4d4d4',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  overflow: 'auto',
                  maxHeight: 300,
                  lineHeight: 1.5,
                }}>
                  {block.code}
                </pre>
              </div>
            )}
          </div>
        ))
      ) : (
        <pre style={{
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          fontFamily: 'var(--font-primary)',
          fontSize: '0.8425rem', color: 'var(--color-text)',
          margin: '4px 0 0 0',
        }}
          dangerouslySetInnerHTML={{ __html: formatExplanation(message.content) }}
        />
      )}
    </div>
  )
}

export interface SourceEditorProps {
  /** Project folder name. */
  project: string
  /** Scope the Source-Code tree to this src-relative subdirectory (e.g.
   *  `transaction/Sales_Order`). Scoped mode lists only that object's code
   *  files and hides the Project Files tab + project ZIP download. */
  scopeDir?: string
  /** Open this file (root-relative path) on mount / when it changes. */
  initialFile?: string
  initialRoot?: 'src' | 'project'
  /** Bump to force re-opening `initialFile` again (e.g. the same path
   *  requested twice after the user closed its editor tab). */
  openNonce?: number
  /** Fill the parent container instead of the full-page viewport layout. */
  embedded?: boolean
}

// The full source editor (file explorer + CodeMirror tabs + AI assistant).
// Rendered by SourceEditorPage as the project-wide /srceditor/:project page,
// and embeddable elsewhere (e.g. the workbench object "Code" tab) with the
// tree scoped to a single object's folder — identical UI both ways.
export function SourceEditor({
  project, scopeDir, initialFile, initialRoot = 'src', openNonce = 0, embedded = false,
}: SourceEditorProps) {
  const preopenFile = initialFile || ''
  const preopenRoot = scopeDir ? 'src' : initialRoot
  const projectName = project || 'Draft Application'

  // Explorer tab state
  const [explorerTab, setExplorerTab] = useState<'src' | 'project'>('src')

  // Separate file tree state for each explorer tab
  const [srcFileTree, setSrcFileTree] = useState<FileNode[]>([])
  const [projectFileTree, setProjectFileTree] = useState<FileNode[]>([])
  const [srcExpandedDirs, setSrcExpandedDirs] = useState<Set<string>>(new Set())
  const [projectExpandedDirs, setProjectExpandedDirs] = useState<Set<string>>(new Set())
  const [loadingSrcTree, setLoadingSrcTree] = useState(true)
  const [loadingProjectTree, setLoadingProjectTree] = useState(false)
  const projectTreeLoadedRef = useRef(false)

  const [leftPanelWidth, setLeftPanelWidth] = useState(260)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)

  // Full-screen toggle (embedded mode): overlays the editor across the whole
  // viewport so an author can focus on the code without the surrounding shell.
  const [fullscreen, setFullscreen] = useState(false)

  // Editor state - activeFile stores composite key (root:path)
  const [openFiles, setOpenFiles] = useState<OpenFile[]>([])
  const [activeFile, setActiveFile] = useState<string | null>(null)
  const [editorSelection, setEditorSelection] = useState('')

  // AI Assistant state
  const [aiPanelOpen, setAiPanelOpen] = useState(false)
  const [aiPrompt, setAiPrompt] = useState('')
  const att = useAttachments()
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([])
  const [aiLoading, setAiLoading] = useState(false)
  const chatEndRef = useRef<HTMLDivElement>(null)

  // Generation mode
  const [generationMode, setGenerationMode] = useState<string>('llm')

  // Load generation mode from settings
  useEffect(() => {
    getSettings().then((s) => {
      setGenerationMode(s.generation_mode || 'llm')
    }).catch(() => { /* use default */ })
  }, [])

  // Claude Code agent hook
  const agent = useClaudeAgent({
    project: projectName,
    onComplete: () => {
      // Extract accumulated text from agent messages and add to chatHistory
      // so it renders with AIChatBubble (with Accept/Copy buttons)
      const agentText = agent.messages
        .filter((m) => m.role === 'assistant')
        .map((m) => m.content)
        .join('')
      if (agentText.trim()) {
        setChatHistory((prev) => [
          ...prev,
          { role: 'assistant', content: agentText.trim() },
        ])
      }
      agent.clearMessages()
    },
  })

  // Comment-only overlay state
  const [showCommentOverlay, setShowCommentOverlay] = useState(false)
  const overlayCheckedFilesRef = useRef<Set<string>>(new Set())

  // Download state
  const [downloading, setDownloading] = useState(false)

  // Resizing state
  const resizeRef = useRef<{ startX: number; startWidth: number } | null>(null)

  // Load src file tree on mount. When `scopeDir` is set the tree is rooted
  // at that subfolder (paths stay src-relative, so open/save/delete work
  // unchanged) — this is what scopes the object "Code" tab to one object.
  useEffect(() => {
    const loadTree = async () => {
      setLoadingSrcTree(true)
      try {
        const data = await getTree(projectName, scopeDir || '', 'src')
        setSrcFileTree(data.data || [])
      } catch {
        toast.error('Failed to load file tree')
      } finally {
        setLoadingSrcTree(false)
      }
    }
    loadTree()
    // Reset project tree loaded flag when project changes
    projectTreeLoadedRef.current = false
    setProjectFileTree([])
  }, [projectName, scopeDir])

  // Auto-open the file passed in `?file=` / `initialFile` once. Runs after
  // the relevant tree finishes loading so we know the path is real before
  // issuing the GET. Switches the explorer tab to match `root` so the file
  // is visible in the sidebar after opening. `openNonce` participates in the
  // attempt key so embedders can re-request the same path.
  const preopenAttemptedRef = useRef<string | null>(null)
  useEffect(() => {
    if (!preopenFile) return
    const attemptKey = `${openNonce}:${preopenRoot}:${preopenFile}`
    if (preopenAttemptedRef.current === attemptKey) return
    if (preopenRoot === 'src' && loadingSrcTree) return
    if (preopenRoot === 'project' && loadingProjectTree && projectTreeLoadedRef.current) return
    preopenAttemptedRef.current = attemptKey
    setExplorerTab(preopenRoot)
    void (async () => {
      try {
        const data = await getFileContent(projectName, preopenFile, preopenRoot)
        const content = data.content || ''
        const name = preopenFile.split('/').pop() || preopenFile
        const key = fileKey(preopenRoot, preopenFile)
        setOpenFiles((prev) => {
          if (prev.some((f) => fileKey(f.root, f.path) === key)) return prev
          return [...prev, {
            path: preopenFile, name, content,
            originalContent: content, modified: false, root: preopenRoot,
          }]
        })
        setActiveFile(key)
      } catch {
        toast.error(`Could not open ${preopenFile}`)
      }
    })()
  }, [preopenFile, preopenRoot, openNonce, projectName, loadingSrcTree, loadingProjectTree])

  // Lazy-load project tree when tab is first clicked (no Project Files tab
  // in scoped mode, so never load it there)
  useEffect(() => {
    if (scopeDir || explorerTab !== 'project' || projectTreeLoadedRef.current) return
    const loadProjectTree = async () => {
      setLoadingProjectTree(true)
      try {
        const data = await getTree(projectName, '', 'project')
        setProjectFileTree(data.data || [])
        projectTreeLoadedRef.current = true
      } catch {
        toast.error('Failed to load project files')
      } finally {
        setLoadingProjectTree(false)
      }
    }
    loadProjectTree()
  }, [explorerTab, projectName, scopeDir])

  // Live-refresh after an AI agent run reports it touched project files —
  // re-fetch the file trees (new/removed files) and reload the content of
  // every open file that has NO unsaved edits, so agent changes show up
  // without a hard browser refresh. Files with local modifications are left
  // untouched so the agent never clobbers in-progress edits.
  const openFilesRef = useRef(openFiles)
  openFilesRef.current = openFiles
  useEffect(() => {
    const onFilesChanged = (e: Event) => {
      const detail = (e as CustomEvent).detail as { project?: string } | undefined
      if (detail?.project && detail.project !== projectName) return

      // 1) Refresh the trees the user has loaded.
      getTree(projectName, scopeDir || '', 'src')
        .then((data) => setSrcFileTree(data.data || []))
        .catch(() => { /* keep old tree on failure */ })
      if (projectTreeLoadedRef.current) {
        getTree(projectName, '', 'project')
          .then((data) => setProjectFileTree(data.data || []))
          .catch(() => { /* keep old tree on failure */ })
      }

      // 2) Reload open, unmodified files.
      for (const f of openFilesRef.current) {
        if (f.modified) continue
        const key = fileKey(f.root, f.path)
        getFileContent(projectName, f.path, f.root)
          .then((data) => {
            const content = data.content || ''
            setOpenFiles((cur) =>
              cur.map((c) =>
                fileKey(c.root, c.path) === key && !c.modified && c.content !== content
                  ? { ...c, content, originalContent: content }
                  : c,
              ),
            )
          })
          .catch(() => { /* file may have been deleted — leave the tab as-is */ })
      }
    }
    window.addEventListener('twasta:agent-files-changed', onFilesChanged as EventListener)
    return () =>
      window.removeEventListener('twasta:agent-files-changed', onFilesChanged as EventListener)
  }, [projectName, scopeDir])

  // Save keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault()
        handleSaveActiveFile()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  })

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [chatHistory, aiLoading])

  const toggleSrcDir = useCallback((path: string) => {
    setSrcExpandedDirs((prev) => {
      const next = new Set(prev)
      next.has(path) ? next.delete(path) : next.add(path)
      return next
    })
  }, [])

  const toggleProjectDir = useCallback((path: string) => {
    setProjectExpandedDirs((prev) => {
      const next = new Set(prev)
      next.has(path) ? next.delete(path) : next.add(path)
      return next
    })
  }, [])

  const handleFileClick = useCallback(async (node: FileNode, root: 'src' | 'project') => {
    const key = fileKey(root, node.path)
    // Dismiss overlay when switching files
    setShowCommentOverlay(false)
    setMobileNavOpen(false)

    // Check if already open
    const existing = openFiles.find((f) => fileKey(f.root, f.path) === key)
    if (existing) {
      setActiveFile(key)
      return
    }

    try {
      const data = await getFileContent(projectName, node.path, root)
      const content = data.content || ''
      const newFile: OpenFile = {
        path: node.path,
        name: node.name,
        content,
        originalContent: content,
        modified: false,
        root,
      }
      setOpenFiles((prev) => [...prev, newFile])
      setActiveFile(key)

      // Check if file is comment-only and auto-open AI assistant (like original code)
      if (content.trim() && !overlayCheckedFilesRef.current.has(key)) {
        overlayCheckedFilesRef.current.add(key)
        setTimeout(() => {
          if (isCommentOnlyContent(content)) {
            setShowCommentOverlay(true)
          }
        }, 0)
      }
    } catch {
      toast.error('Failed to load file')
    }
  }, [projectName, openFiles])

  const handleCloseFile = useCallback((key: string, e?: React.MouseEvent) => {
    e?.stopPropagation()
    setOpenFiles((prev) => prev.filter((f) => fileKey(f.root, f.path) !== key))
    if (activeFile === key) {
      setActiveFile(() => {
        const remaining = openFiles.filter((f) => fileKey(f.root, f.path) !== key)
        return remaining.length > 0 ? fileKey(remaining[remaining.length - 1].root, remaining[remaining.length - 1].path) : null
      })
    }
  }, [activeFile, openFiles])

  const handleDeleteFile = useCallback(async (node: FileNode, root: 'src' | 'project') => {
    if (!window.confirm(`Delete "${node.name}"?`)) return
    try {
      await deleteFile(projectName, node.path, root)
      toast.success(`Deleted ${node.name}`)
      // Close the file if it's open
      const key = fileKey(root, node.path)
      handleCloseFile(key)
      // Refresh the tree
      if (root === 'src') {
        const data = await getTree(projectName, scopeDir || '', 'src')
        setSrcFileTree(data.data || [])
      } else {
        const data = await getTree(projectName, '', 'project')
        setProjectFileTree(data.data || [])
      }
    } catch {
      toast.error('Failed to delete file')
    }
  }, [projectName, scopeDir, handleCloseFile])

  const handleEditorChange = useCallback((value: string) => {
    if (!activeFile) return
    setOpenFiles((prev) =>
      prev.map((f) =>
        fileKey(f.root, f.path) === activeFile ? { ...f, content: value, modified: true } : f
      )
    )
  }, [activeFile])

  const handleSaveActiveFile = useCallback(async () => {
    if (!activeFile) return
    const file = openFiles.find((f) => fileKey(f.root, f.path) === activeFile)
    if (!file) return

    try {
      await saveFile(projectName, file.path, file.content, file.root)
      setOpenFiles((prev) =>
        prev.map((f) =>
          fileKey(f.root, f.path) === activeFile ? { ...f, modified: false, originalContent: f.content } : f
        )
      )
      toast.success('File saved')
    } catch {
      toast.error('Failed to save file')
    }
  }, [activeFile, openFiles, projectName])

  const handleAiSuggest = useCallback(async (customPrompt?: string) => {
    const promptText = customPrompt || aiPrompt.trim()
    // Attachments only apply to a user-initiated send (not programmatic
    // customPrompt calls like the comment-overlay generator).
    const wireAttachments = customPrompt ? [] : att.attachments.map(toWire)
    if (!promptText && wireAttachments.length === 0) return
    const file = openFiles.find((f) => fileKey(f.root, f.path) === activeFile)
    if (!customPrompt) {
      setAiPrompt('')
      att.clear()
    }

    // Claude Code Agent mode
    if (generationMode === 'claude_code') {
      // Add user message to chat history
      setChatHistory((prev) => [...prev, { role: 'user', content: promptText }])
      agent.startTask('code_assist', {
        user_input: promptText,
        code: editorSelection || (file?.content || ''),
        language: file ? getFileTypeLabel(file.name) : 'text',
        file_path: file?.path || '',
        chat_history: chatHistory.map((m) => ({ role: m.role, content: m.content })),
        description: promptText,
        ...(wireAttachments.length ? { attachments: wireAttachments } : {}),
      })
      return
    }

    // LLM API mode
    const userMessage: ChatMessage = { role: 'user', content: promptText }
    setChatHistory((prev) => [...prev, userMessage])
    setAiLoading(true)

    try {
      const result = await aiSuggest({
        project: projectName,
        userInput: promptText,
        selectedText: editorSelection || (file?.content || ''),
        fileType: file ? getFileTypeLabel(file.name) : 'text',
        chatHistory: chatHistory.map((m) => ({ role: m.role, content: m.content })),
        model: '',
        filePath: file?.path || '',
        ...(wireAttachments.length ? { attachments: wireAttachments } : {}),
      })

      // Backend returns { message: "full AI text", suggestion: {original, suggested} | null }
      const responseText = typeof result.message === 'string'
        ? result.message
        : typeof result.content === 'string'
          ? result.content
          : typeof result.code === 'string'
            ? result.code
            : JSON.stringify(result, null, 2)

      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: responseText,
      }
      setChatHistory((prev) => [...prev, assistantMessage])
    } catch {
      const errorMessage: ChatMessage = {
        role: 'assistant',
        content: 'Sorry, I encountered an error generating a suggestion. Please try again.',
      }
      setChatHistory((prev) => [...prev, errorMessage])
    } finally {
      setAiLoading(false)
    }
  }, [aiPrompt, activeFile, openFiles, projectName, editorSelection, chatHistory, generationMode, agent, att])

  // Accept code: replace editor content (if selection exists, replace selection; otherwise replace full content)
  const handleAcceptCode = useCallback((code: string) => {
    if (!activeFile) {
      toast.error('No file open')
      return
    }
    setOpenFiles((prev) =>
      prev.map((f) => {
        if (fileKey(f.root, f.path) !== activeFile) return f
        // If there's a selection, we can't easily replace just the selection from React state,
        // so we replace the full content with the AI code
        return { ...f, content: code, modified: true }
      })
    )
    toast.success('Code applied to editor')
  }, [activeFile])

  // Copy code to clipboard
  const handleCopyCode = useCallback((code: string) => {
    navigator.clipboard.writeText(code)
    toast.success('Code copied to clipboard')
  }, [])

  // Delete a message and its related response
  const handleDeleteMessage = useCallback((idx: number) => {
    setChatHistory(prev => {
      const msg = prev[idx]
      if (msg.role === 'user') {
        // Delete user message and next assistant response if exists
        const next = prev[idx + 1]
        if (next && next.role === 'assistant') {
          return prev.filter((_, i) => i !== idx && i !== idx + 1)
        }
        return prev.filter((_, i) => i !== idx)
      } else {
        // Delete assistant message and previous user message if exists
        const prevMsg = prev[idx - 1]
        if (prevMsg && prevMsg.role === 'user') {
          return prev.filter((_, i) => i !== idx && i !== idx - 1)
        }
        return prev.filter((_, i) => i !== idx)
      }
    })
  }, [])

  // Handle "Generate Code" from comment-only overlay
  const handleGenerateFromComments = useCallback(() => {
    setShowCommentOverlay(false)
    setAiPanelOpen(true)
    handleAiSuggest('Generate code based on the comments in this file')
  }, [handleAiSuggest])

  // Handle "Let's Chat" from overlay
  const handleLetsChatFromOverlay = useCallback(() => {
    setShowCommentOverlay(false)
    setAiPanelOpen(true)
  }, [])

  // Handle ZIP download
  const handleDownloadAll = useCallback(async () => {
    setDownloading(true)
    try {
      await downloadAll(projectName)
      toast.success('Project downloaded as ZIP')
    } catch {
      toast.error('Failed to download project')
    } finally {
      setDownloading(false)
    }
  }, [projectName])

  // Mouse drag resize for left panel
  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    resizeRef.current = { startX: e.clientX, startWidth: leftPanelWidth }
    const handleMouseMove = (ev: MouseEvent) => {
      if (!resizeRef.current) return
      const diff = ev.clientX - resizeRef.current.startX
      const newWidth = Math.max(180, Math.min(500, resizeRef.current.startWidth + diff))
      setLeftPanelWidth(newWidth)
    }
    const handleMouseUp = () => {
      resizeRef.current = null
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }, [leftPanelWidth])

  const { theme } = useThemeStore()
  const activeOpenFile = openFiles.find((f) => fileKey(f.root, f.path) === activeFile)
  const languageExtensions = activeOpenFile ? getLanguageExtension(activeOpenFile.name) : []

  return (
    <div
      className="split-layout"
      style={{
        display: 'flex', overflow: 'hidden', padding: 0,
        // Full screen wins regardless of mode: overlay the whole viewport.
        // Otherwise: page mode fills the viewport below the top bar (escaping
        // the page padding); embedded mode just fills whatever box hosts it.
        ...(fullscreen
          ? { position: 'fixed', inset: 0, zIndex: 1000, margin: 0, background: 'var(--color-bg)' }
          : embedded
            ? { flex: 1, minHeight: 0, margin: 0 }
            : { height: 'calc(100vh - 52px)', margin: '0 -1.5rem -1.5rem' }),
      }}
    >
      <style>{`.file-tree-item:hover .file-delete-btn { display: flex !important; }`}</style>
      {/* Mobile nav backdrop */}
      {mobileNavOpen && <div className="split-nav-backdrop" onClick={() => setMobileNavOpen(false)} />}

      {/* ── Left panel - File tree ── */}
      <div className={`split-nav ${mobileNavOpen ? '' : 'split-nav-hidden'}`} style={{
        width: leftPanelWidth,
        flexShrink: 0,
        background: 'var(--color-bg)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRight: '1px solid var(--color-border)',
      }}>
        {/* Explorer header */}
        <div style={{
          padding: '0.625rem 0.75rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.375rem',
          }}>
            <Folder size={14} style={{ color: 'var(--color-accent)' }} />
            <span style={{
              fontSize: '0.8125rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: 'var(--color-text-secondary)',
            }}>Explorer</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.125rem' }}>
            {!scopeDir && (
              <button
                className="btn btn-ghost btn-sm"
                onClick={handleDownloadAll}
                disabled={downloading}
                title="Download project as ZIP"
                style={{ padding: '2px 4px' }}
              >
                {downloading ? (
                  <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <Download size={13} />
                )}
              </button>
            )}
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setFullscreen((f) => !f)}
              title={fullscreen ? 'Restore' : 'Full screen'}
              style={{ padding: '2px 4px' }}
            >
              {fullscreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
            </button>
          </div>
        </div>

        {/* Project name badge */}
        <div style={{
          padding: '0 0.75rem 0.5rem',
          flexShrink: 0,
        }}>
          <div style={{
            padding: '0.375rem 0.625rem',
            background: 'var(--color-bg-secondary)',
            borderRadius: 'var(--radius-md)',
            fontSize: '0.8125rem',
            fontWeight: 500,
            color: 'var(--color-text)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.375rem',
            overflow: 'hidden',
          }}>
            <FolderOpen size={13} style={{ color: 'var(--color-warning, #9a6700)', flexShrink: 0 }} />
            <span className="truncate" title={scopeDir ? `${projectName} / ${scopeDir}` : projectName}>
              {scopeDir
                ? (scopeDir.split('/').pop() || scopeDir).replace(/_/g, ' ')
                : projectName}
            </span>
          </div>
        </div>

        {/* Explorer tab bar — object-scoped mode is src-only, no tab bar */}
        {!scopeDir && (
          <div style={{
            display: 'flex',
            flexShrink: 0,
            borderBottom: '1px solid var(--color-border)',
          }}>
            {(['src', 'project'] as const).map((tab) => {
              const isActive = explorerTab === tab
              return (
                <button
                  key={tab}
                  onClick={() => setExplorerTab(tab)}
                  style={{
                    flex: 1,
                    padding: '0.375rem 0.5rem',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    background: 'none',
                    color: isActive ? 'var(--color-accent)' : 'var(--color-text-secondary)',
                    borderBottom: isActive ? '2px solid var(--color-accent)' : '2px solid transparent',
                    cursor: 'pointer',
                    transition: 'color var(--transition-fast), border-color var(--transition-fast)',
                  }}
                >
                  {tab === 'src' ? 'Source Code' : 'Project Files'}
                </button>
              )
            })}
          </div>
        )}

        {/* File tree */}
        <div style={{ flex: 1, overflow: 'auto', padding: '0.25rem 0' }}>
          {explorerTab === 'src' ? (
            loadingSrcTree ? (
              <div className="flex items-center justify-center p-4">
                <Loader2 size={18} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-accent)' }} />
              </div>
            ) : srcFileTree.length > 0 ? (
              srcFileTree
                .slice()
                .sort((a, b) => {
                  if (a.is_dir === b.is_dir) return a.name.localeCompare(b.name)
                  return a.is_dir ? -1 : 1
                })
                .map((node) => (
                  <FileTreeNode
                    key={node.path}
                    node={node}
                    depth={0}
                    onFileClick={(n) => handleFileClick(n, 'src')}
                    onFileDelete={(n) => handleDeleteFile(n, 'src')}
                    onFileDownload={(n) => downloadFile(projectName, n.path, 'src').catch(() => toast.error('Download failed'))}
                    expandedDirs={srcExpandedDirs}
                    toggleDir={toggleSrcDir}
                  />
                ))
            ) : (
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                gap: '0.5rem', padding: '2rem 1rem', color: 'var(--color-text-secondary)',
              }}>
                <File size={24} style={{ opacity: 0.3 }} />
                <p style={{ fontSize: '0.875rem', margin: 0 }}>No source files found</p>
              </div>
            )
          ) : (
            loadingProjectTree ? (
              <div className="flex items-center justify-center p-4">
                <Loader2 size={18} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-accent)' }} />
              </div>
            ) : projectFileTree.length > 0 ? (
              projectFileTree
                .slice()
                .sort((a, b) => {
                  if (a.is_dir === b.is_dir) return a.name.localeCompare(b.name)
                  return a.is_dir ? -1 : 1
                })
                .map((node) => (
                  <FileTreeNode
                    key={node.path}
                    node={node}
                    depth={0}
                    onFileClick={(n) => handleFileClick(n, 'project')}
                    onFileDelete={(n) => handleDeleteFile(n, 'project')}
                    onFileDownload={(n) => downloadFile(projectName, n.path, 'project').catch(() => toast.error('Download failed'))}
                    expandedDirs={projectExpandedDirs}
                    toggleDir={toggleProjectDir}
                  />
                ))
            ) : (
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                gap: '0.5rem', padding: '2rem 1rem', color: 'var(--color-text-secondary)',
              }}>
                <File size={24} style={{ opacity: 0.3 }} />
                <p style={{ fontSize: '0.875rem', margin: 0 }}>No project files found</p>
              </div>
            )
          )}
        </div>
      </div>

      {/* Resize handle */}
      <div
        onMouseDown={handleResizeMouseDown}
        style={{
          width: 3,
          cursor: 'col-resize',
          background: 'transparent',
          flexShrink: 0,
          zIndex: 10,
          marginLeft: -1,
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-accent)')}
        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
      />

      {/* Mobile floating nav toggle */}
      <button
        className="split-nav-toggle"
        onClick={() => setMobileNavOpen(true)}
      >
        <span className="material-icons" style={{ fontSize: 17 }}>folder_open</span>
        Files
      </button>

      {/* ── Center panel - Editor ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
        {/* Toolbar row: tabs + actions */}
        <div style={{
          display: 'flex',
          alignItems: 'stretch',
          background: 'var(--color-bg-secondary)',
          flexShrink: 0,
          minHeight: 38,
        }}>
          {/* Tabs */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            overflow: 'auto',
            gap: 0,
          }}>
            {openFiles.length === 0 && (
              <span style={{
                padding: '0 0.75rem',
                fontSize: '0.8125rem',
                color: 'var(--color-text-secondary)',
              }}>
                No files open
              </span>
            )}
            {openFiles.map((f) => {
              const key = fileKey(f.root, f.path)
              const isActive = activeFile === key
              const tabLabel = f.root === 'project' ? f.name : f.name
              const tabTitle = f.root === 'project' ? `[Project] ${f.path}` : `src/${f.path}`
              return (
                <div
                  key={key}
                  onClick={() => setActiveFile(key)}
                  title={tabTitle}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                    padding: '0 0.75rem',
                    height: '100%',
                    fontSize: '0.875rem',
                    color: isActive ? 'var(--color-text)' : 'var(--color-text-secondary)',
                    background: isActive ? 'var(--color-bg)' : 'transparent',
                    borderBottom: isActive ? '2px solid var(--color-accent)' : '2px solid transparent',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    transition: 'background var(--transition-fast)',
                  }}
                >
                  <File size={12} style={{ flexShrink: 0 }} />
                  <span>{tabLabel}</span>
                  {f.root === 'project' && (
                    <span style={{
                      fontSize: '0.8125rem',
                      padding: '0 3px',
                      borderRadius: 3,
                      background: 'var(--color-bg-secondary)',
                      color: 'var(--color-text-secondary)',
                      flexShrink: 0,
                      lineHeight: '14px',
                    }}>P</span>
                  )}
                  {f.modified && (
                    <span style={{
                      width: 6, height: 6,
                      borderRadius: '50%',
                      background: 'var(--color-accent)',
                      flexShrink: 0,
                    }} />
                  )}
                  <button
                    onClick={(e) => handleCloseFile(key, e)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      width: 18, height: 18,
                      borderRadius: 'var(--radius-sm)',
                      background: 'none',
                      color: 'var(--color-text-secondary)',
                      marginLeft: '0.125rem',
                      flexShrink: 0,
                      opacity: 0.6,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-secondary)'; e.currentTarget.style.opacity = '1' }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.opacity = '0.6' }}
                  >
                    <X size={12} />
                  </button>
                </div>
              )
            })}
          </div>

          {/* Actions group */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
            padding: '0 0.5rem',
            flexShrink: 0,
            borderLeft: openFiles.length > 0 ? '1px solid var(--color-border)' : 'none',
          }}>
            {activeOpenFile && (
              <button
                className={activeOpenFile.modified ? 'btn btn-primary btn-sm' : 'btn btn-ghost btn-sm'}
                onClick={handleSaveActiveFile}
                title="Save (Ctrl+S)"
                style={{ padding: '0.25rem 0.625rem', fontSize: '0.8125rem', gap: '0.3rem' }}
              >
                <Save size={13} />
                Save
              </button>
            )}
            <button
              onClick={() => setAiPanelOpen(!aiPanelOpen)}
              className="btn btn-ghost btn-sm"
              title="Toggle AI Assistant"
              style={{
                padding: '0.25rem 0.5rem',
                fontSize: '0.8125rem',
                color: aiPanelOpen ? 'var(--color-accent)' : undefined,
              }}
            >
              {aiPanelOpen ? <PanelRightClose size={13} /> : <PanelRightOpen size={13} />}
            </button>
          </div>
        </div>

        {/* Editor area */}
        <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
          {activeOpenFile ? (
            <>
              <CodeMirror
                value={activeOpenFile.content}
                onChange={handleEditorChange}
                onUpdate={(viewUpdate) => {
                  const sel = viewUpdate.state.selection.main
                  if (sel.from !== sel.to) {
                    setEditorSelection(viewUpdate.state.sliceDoc(sel.from, sel.to))
                  } else {
                    setEditorSelection('')
                  }
                }}
                extensions={languageExtensions}
                theme={theme === 'dark' ? oneDark : 'light'}
                height="100%"
                style={{ height: '100%' }}
                basicSetup={{
                  lineNumbers: true,
                  foldGutter: true,
                  highlightActiveLine: true,
                  autocompletion: true,
                  bracketMatching: true,
                  closeBrackets: true,
                  indentOnInput: true,
                }}
              />

              {/* Comment-only overlay */}
              {showCommentOverlay && (
                <div style={{
                  position: 'absolute', inset: 0,
                  background: 'rgba(3, 14, 21, 0.35)',
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center',
                  zIndex: 20,
                  backdropFilter: 'blur(2px)',
                }}>
                  <div style={{
                    background: 'var(--color-bg-secondary)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-lg)',
                    padding: '1.25rem 1.5rem',
                    maxWidth: 520,
                    width: '90%',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                      <MessageSquare size={18} style={{ color: 'var(--color-accent)' }} />
                      <span style={{ fontWeight: 600, fontSize: '0.9625rem', color: 'var(--color-text)' }}>
                        This file contains only comments
                      </span>
                    </div>

                    {/* Show extracted comments */}
                    <div style={{
                      background: 'var(--color-bg)',
                      border: '1px solid var(--color-border)',
                      borderRadius: 'var(--radius-md)',
                      padding: '0.625rem 0.75rem',
                      marginBottom: '0.75rem',
                      maxHeight: 180,
                      overflow: 'auto',
                    }}>
                      <pre style={{
                        margin: 0,
                        fontSize: '0.8125rem',
                        fontFamily: 'monospace',
                        color: 'var(--color-text-secondary)',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        lineHeight: 1.5,
                      }}>
                        {activeOpenFile?.content.trim() || ''}
                      </pre>
                    </div>

                    <p style={{
                      fontSize: '0.875rem',
                      color: 'var(--color-text-secondary)',
                      margin: '0 0 1rem 0',
                    }}>
                      Would you like AI to generate code based on these comments?
                    </p>

                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={handleGenerateFromComments}
                        style={{ padding: '0.5rem 1rem' }}
                      >
                        Generate Code
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={handleLetsChatFromOverlay}
                        style={{ padding: '0.5rem 1rem' }}
                      >
                        Let's Chat
                      </button>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => setShowCommentOverlay(false)}
                        style={{ padding: '0.5rem 1rem' }}
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-3" style={{ color: 'var(--color-text-secondary)' }}>
                <File size={48} style={{ opacity: 0.15 }} />
                <p style={{ margin: 0, fontSize: '0.9375rem' }}>Select a file from the explorer to edit</p>
              </div>
            </div>
          )}
        </div>

        {/* Status bar */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 0.75rem',
          background: 'var(--color-bg-secondary)',
          fontSize: '0.8125rem',
          color: 'var(--color-text-secondary)',
          flexShrink: 0,
          height: 22,
          borderTop: '1px solid var(--color-border)',
        }}>
          <span style={{ opacity: 0.7 }}>
            {activeOpenFile
              ? `${activeOpenFile.root === 'project' ? '' : 'src/'}${activeOpenFile.path}`
              : 'No file open'}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {activeOpenFile && (
              <span style={{
                fontSize: '0.8125rem',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                opacity: 0.7,
              }}>
                {getFileTypeLabel(activeOpenFile.name)}
              </span>
            )}
            {activeOpenFile?.modified && (
              <span style={{ color: 'var(--color-accent)', fontWeight: 500 }}>Modified</span>
            )}
          </div>
        </div>
      </div>

      {/* ── Right panel - AI Assistant ── */}
      {aiPanelOpen && (
        <div className="responsive-panel" style={{
          width: 380,
          flexShrink: 0,
          background: 'var(--color-bg-secondary)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          animation: 'slideInRight 200ms ease-out',
          borderLeft: '1px solid var(--color-border)',
        }}>
          {/* AI Header */}
          <div style={{
            padding: '0.625rem 0.75rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
            background: 'rgba(76, 141, 255, 0.04)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <MessageSquare size={15} style={{ color: 'var(--color-accent)' }} />
              <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>
                AI Assistant{generationMode === 'claude_code' ? ' (Agent)' : ''}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 2 }}>
              {agent.isStreaming && generationMode === 'claude_code' && (
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={agent.cancelTask}
                  style={{ padding: '2px 6px', color: 'var(--color-error, #cf222e)' }}
                  title="Cancel"
                >
                  <X size={12} />
                </button>
              )}
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => { setChatHistory([]); if (generationMode === 'claude_code') agent.clearMessages() }}
                style={{ padding: '2px 6px' }}
                title="Clear chat"
              >
                <Trash2 size={12} />
              </button>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setAiPanelOpen(false)}
                style={{ padding: '2px 6px' }}
                title="Close"
              >
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Chat messages */}
          <div style={{ flex: 1, overflow: 'auto', padding: '0.75rem' }}>
            {chatHistory.length === 0 && !agent.isStreaming ? (
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', gap: '0.5rem', height: '100%',
                color: 'var(--color-text-secondary)', textAlign: 'center',
              }}>
                <MessageSquare size={32} style={{ opacity: 0.15 }} />
                <p style={{ fontSize: '0.875rem', margin: 0 }}>
                  Ask for code suggestions, explanations, or help with your code.
                </p>
                <p style={{ fontSize: '0.8125rem', margin: 0, opacity: 0.7 }}>
                  Select text in the editor for context-aware assistance.
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
                {chatHistory.map((msg, idx) => (
                  <AIChatBubble
                    key={idx}
                    message={msg}
                    onAcceptCode={handleAcceptCode}
                    onCopyCode={handleCopyCode}
                    onDeleteMessage={() => handleDeleteMessage(idx)}
                  />
                ))}
                {/* Agent streaming indicator */}
                {agent.isStreaming && generationMode === 'claude_code' && (
                  <div style={{
                    padding: '0.625rem 0.75rem',
                    borderRadius: 'var(--radius-md)',
                    background: 'var(--color-bg)',
                    border: '1px solid var(--color-border)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: 4 }}>
                      <Loader2 size={14} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-accent)' }} />
                      <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                        {agent.agentProgress?.status === 'responding' ? 'Generating code...' : 'Thinking...'}
                      </span>
                    </div>
                    {/* Show live streamed text */}
                    {agent.messages.filter((m) => m.role === 'assistant').map((m) => m.content).join('').trim() && (
                      <pre style={{
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        fontFamily: 'monospace', fontSize: '0.8125rem',
                        color: 'var(--color-text)', margin: '4px 0 0 0',
                        maxHeight: 300, overflow: 'auto',
                        background: 'var(--color-bg-secondary)', padding: '0.5rem',
                        borderRadius: 'var(--radius-sm)',
                      }}>
                        {agent.messages.filter((m) => m.role === 'assistant').map((m) => m.content).join('')}
                      </pre>
                    )}
                  </div>
                )}
                {aiLoading && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                    padding: '0.5rem',
                  }}>
                    <Loader2 size={14} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-accent)' }} />
                    <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>Thinking...</span>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
            )}
          </div>

          {/* AI Input */}
          <div style={{
            padding: '0.75rem',
            flexShrink: 0,
            background: 'var(--color-bg)',
          }}>
            {editorSelection && (
              <div style={{
                padding: '0.375rem 0.5rem',
                background: 'var(--color-bg-secondary)',
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.8125rem',
                color: 'var(--color-text-secondary)',
                marginBottom: '0.5rem',
                maxHeight: 60,
                overflow: 'hidden',
                border: '1px solid var(--color-border)',
              }}>
                <span style={{ fontWeight: 600, fontSize: '0.8125rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--color-accent)' }}>Selected</span>
                <div style={{ marginTop: 2, lineHeight: 1.3 }}>
                  {editorSelection.substring(0, 100)}{editorSelection.length > 100 ? '...' : ''}
                </div>
              </div>
            )}
            <AttachmentChips
              attachments={att.attachments}
              onRemove={att.remove}
              disabled={aiLoading || agent.isStreaming}
            />
            <div style={{ display: 'flex', gap: '0.375rem' }}>
              <input
                ref={att.fileInputRef}
                type="file"
                multiple
                accept={ATTACHMENT_ACCEPT}
                onChange={att.onFileInputChange}
                style={{ display: 'none' }}
              />
              <button
                type="button"
                onClick={att.openFilePicker}
                title="Attach files or images"
                disabled={aiLoading || agent.isStreaming}
                style={{
                  flexShrink: 0, alignSelf: 'flex-end', width: 40, height: 40,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  background: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)',
                  border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                }}
              >
                <Paperclip size={15} />
              </button>
              <textarea
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder="Ask AI for help..."
                {...att.inputHandlers}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' && (e.metaKey || e.ctrlKey)) || (e.key === 'Enter' && !e.shiftKey)) {
                    e.preventDefault()
                    handleAiSuggest()
                  }
                }}
                disabled={aiLoading || agent.isStreaming}
                style={{
                  flex: 1, fontSize: '0.875rem', padding: '0.5rem 0.625rem',
                  background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-md)', color: 'var(--color-text)',
                  resize: 'none', minHeight: 40, maxHeight: 120,
                  fontFamily: 'var(--font-primary)',
                }}
                rows={2}
              />
              <button
                className="btn btn-primary btn-sm"
                onClick={() => handleAiSuggest()}
                disabled={aiLoading || agent.isStreaming || (!aiPrompt.trim() && att.attachments.length === 0)}
                style={{ flexShrink: 0, alignSelf: 'flex-end', padding: '0.5rem 0.625rem' }}
              >
                {(aiLoading || agent.isStreaming) ? (
                  <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <Send size={14} />
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Full-page /srceditor/:project route — the project-wide code editor.
// Callers can tag the URL with `?file=...&root=src` to land directly on a
// file without drilling into the tree manually.
export default function SourceEditorPage() {
  const { project } = useParams<{ project: string }>()
  useRunOnlyRedirect(project)
  const [searchParams] = useSearchParams()
  const preopenFile = searchParams.get('file') || ''
  const preopenRoot = (searchParams.get('root') === 'project' ? 'project' : 'src') as 'src' | 'project'
  const projectName = project || 'Draft Application'
  const { currentProject, selectProject } = useProjectStore()

  // Restore currentProject in store from URL param on refresh
  useEffect(() => {
    if (project && currentProject?.application.folder_name !== project) {
      selectProject(project)
    }
  }, [project, currentProject, selectProject])

  return (
    <SourceEditor
      project={projectName}
      initialFile={preopenFile || undefined}
      initialRoot={preopenRoot}
    />
  )
}
